import React, { useCallback, useEffect, useState, useRef } from 'react'
import Styles from './MyVideo.module.css'
import { usePreMeetingContext } from '../../../context/PreMeetingContext'
import { getItem } from '../../../helpers/localStorage'
import { useZoomContext } from '../../../context/ZoomContext'
import { usePermissions } from '../../../context/PermissionsContext'
import { useVideoPlayback } from '../hooks/useVideoPlayback'

// Define common video constraints
const VIDEO_CONSTRAINTS = {
    high: { width: { ideal: 1280 }, height: { ideal: 720 }, aspectRatio: { ideal: 16 / 9 } },
    medium: { width: { ideal: 640 }, height: { ideal: 360 }, aspectRatio: { ideal: 16 / 9 } },
    low: { width: { ideal: 320 }, height: { ideal: 180 }, aspectRatio: { ideal: 16 / 9 } },
}

export const MyVideo: React.FC = () => {
    const { zoomClient } = useZoomContext()
    const { localVideoTrack: video, isInVBMode, setIsInVBMode, videoRef, canvasRef } = usePreMeetingContext()
    const { microphone, camera, devicesBusy, devicesNotFound } = usePermissions()
    const permissionGranted = microphone && camera

    const [isVideoReady, setIsVideoReady] = useState(false)
    const [isInitialized, setIsInitialized] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const savedVfx = getItem<string>('vfx')
    const flags = useRef({
        isInitializing: false,
        isStartingVideo: false,
        lastVBMode: isInVBMode,
        currentQualityLevel: 'high' as keyof typeof VIDEO_CONSTRAINTS,
        refsReady: false,
        retryCount: 0,
    })

    const elementRefs = useRef<{
        video: HTMLVideoElement | null
        canvas: HTMLCanvasElement | null
    }>({ video: null, canvas: null })

    useVideoPlayback({ isVideoReady, setIsVideoReady })

    const initZoomClient = useCallback(async () => {
        if (!zoomClient || flags.current.isInitializing || isInitialized) return
        flags.current.isInitializing = true
        let retries = 3
        while (retries > 0) {
            try {
                await zoomClient.init('en-US', 'Global', {
                    webEndpoint: 'zoom.us',
                    stayAwake: true,
                    leaveOnPageUnload: true,
                    patchJsMedia: true,
                })
                setIsInitialized(true)
                setErrorMessage(null)
                break
            } catch (error) {
                console.error('Error initializing Zoom client:', error)
                retries--
                if (retries === 0) {
                    setErrorMessage('Failed to initialize Zoom client after retries.')
                    setIsInitialized(false)
                }
                await new Promise(resolve => setTimeout(resolve, 1000))
            }
        }
        flags.current.isInitializing = false
    }, [zoomClient, isInitialized])

    const getVBConfig = useCallback(() => {
        if (!savedVfx) return undefined
        try {
            return { imageUrl: savedVfx, cropped: false }
        } catch {
            console.warn('Invalid virtual background URL in local storage, ignoring:', savedVfx)
            return undefined
        }
    }, [savedVfx])

    const attemptVideoStart = useCallback(
        async (element: HTMLVideoElement | HTMLCanvasElement, vbConfig?: any) => {
            const qualities = ['high', 'medium', 'low'] as const
            let currentQualityIndex = qualities.indexOf(flags.current.currentQualityLevel)

            for (let i = currentQualityIndex; i < qualities.length; i++) {
                const quality = qualities[i]
                try {
                    await video.start(element, { ...VIDEO_CONSTRAINTS[quality], ...(vbConfig || {}) })
                    flags.current.currentQualityLevel = quality
                    if (element instanceof HTMLCanvasElement) {
                        element.width = VIDEO_CONSTRAINTS[quality].width.ideal;
                        element.height = VIDEO_CONSTRAINTS[quality].height.ideal;
                    }
                    return
                } catch (error) {
                    console.warn(`Failed with ${quality} quality:`, error)
                    if (i === qualities.length - 1) {
                        try {
                            await video.start(element, vbConfig) // Final attempt with no constraints
                        } catch (finalError: unknown) {
                            if (finalError instanceof Error) {
                                throw new Error(`Video start failed: ${finalError.message}`)
                            }
                            throw new Error('Video start failed: Unknown error')
                        }
                    }
                }
            }
        },
        [video]
    )

    const safeVideoStop = useCallback(async () => {
        if (!video?.isVideoStarted) return
        try {
            await video.stop()
            await new Promise(resolve => setTimeout(resolve, 500))
        } catch (error) {
            console.warn('Error stopping video:', error)
        }
    }, [video])

    const ensureRefsReady = useCallback(async () => {
        if (elementRefs.current.video && elementRefs.current.canvas) {
            flags.current.refsReady = true
            return true
        }

        const maxAttempts = 10
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (videoRef.current && canvasRef.current) {
                elementRefs.current.video = videoRef.current
                elementRefs.current.canvas = canvasRef.current
                flags.current.refsReady = true
                return true
            }
            await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(1.5, attempt)))
        }
        setErrorMessage('Video or canvas elements not ready.')
        return false
    }, [videoRef, canvasRef])

    const startVideo = useCallback(async () => {
        if (!video || flags.current.isStartingVideo || !permissionGranted) return
        if (isCleaningUp.current) await new Promise(resolve => setTimeout(resolve, 1000));
        
        flags.current.isStartingVideo = true

        try {
            const refsReady = await ensureRefsReady()
            if (!refsReady) {
                throw new Error('DOM elements not ready.')
            }

            const videoElement = elementRefs.current.video
            const canvasElement = elementRefs.current.canvas
            if (!videoElement || !canvasElement) {
                throw new Error('Missing DOM elements.')
            }

            await safeVideoStop()

            const vbConfig = getVBConfig()
            const useVB = savedVfx && vbConfig
            setIsInVBMode(!!useVB)

            const targetElement = useVB ? canvasElement : videoElement
            await attemptVideoStart(targetElement, useVB ? vbConfig : undefined)
            flags.current.lastVBMode = !!useVB
            setIsVideoReady(true)
            setErrorMessage(null)
        } catch (error) {
            console.error('Error starting video:', error)
            setIsVideoReady(false)
            setErrorMessage(`Failed to start video: ${error instanceof Error ? error.message : String(error)}`)
        } finally {
            flags.current.isStartingVideo = false
        }
    }, [
        video,
        permissionGranted,
        savedVfx,
        setIsInVBMode,
        attemptVideoStart,
        getVBConfig,
        safeVideoStop,
        ensureRefsReady,
    ])

    useEffect(() => {
        if (!navigator.permissions?.query || !video) return;
        const checkPermissions = async () => {
            const camStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
            camStatus.onchange = () => {
                if (camStatus.state === 'denied' && video.isVideoStarted) {
                    setErrorMessage('Camera permission revoked.');
                    setIsVideoReady(false);
                    startVideo(); // Retry if permissions restored
                }
            };
        };
        checkPermissions();
    }, [video, startVideo]);

    useEffect(() => {
        const initVideo = async () => {
            if (!permissionGranted || !video || !zoomClient) {
                setErrorMessage('Permissions or dependencies missing.')
                return
            }
            if (!isInitialized) await initZoomClient()
            if (isInitialized) await startVideo()
        }
        initVideo()
    }, [permissionGranted, isInitialized, video, zoomClient, initZoomClient, startVideo])

    useEffect(() => {
        const handleVBModeChange = async () => {
            if (!video?.isVideoStarted || flags.current.lastVBMode === isInVBMode || !permissionGranted) return
            try {
                const refsReady = await ensureRefsReady()
                if (!refsReady) return

                const videoElement = elementRefs.current.video
                const canvasElement = elementRefs.current.canvas
                if (!videoElement || !canvasElement) return

                await safeVideoStop()
                const config = isInVBMode ? getVBConfig() : undefined
                await attemptVideoStart(isInVBMode ? canvasElement : videoElement, config)
                flags.current.lastVBMode = isInVBMode
                setIsVideoReady(true)
            } catch (error) {
                console.error('Error switching VB mode:', error)
                setErrorMessage(`VB mode switch failed: ${error instanceof Error ? error.message : String(error)}`)
            }
        }
        handleVBModeChange()
    }, [isInVBMode, video, permissionGranted, getVBConfig, attemptVideoStart, safeVideoStop, ensureRefsReady])

    const isCleaningUp = useRef(false)

    useEffect(() => {
        return () => {
            if (video?.isVideoStarted) {
                isCleaningUp.current = true
                video
                    .stop()
                    .catch(error => console.warn('Cleanup error:', error))
                    .finally(() => {
                        isCleaningUp.current = false
                    })
            }
        }
    }, [video])

    useEffect(() => {
        if (videoRef.current && canvasRef.current) {
            elementRefs.current.video = videoRef.current
            elementRefs.current.canvas = canvasRef.current
            flags.current.refsReady = true
            // Adjust canvas size based on current quality
            const quality = VIDEO_CONSTRAINTS[flags.current.currentQualityLevel]
            canvasRef.current.width = quality.width.ideal
            canvasRef.current.height = quality.height.ideal
        }
    }, [videoRef, canvasRef])

    // Feature detection for basic browser compatibility
    const isMediaSupported = !devicesBusy && !devicesNotFound && camera && microphone


    if (!isMediaSupported) {
        return <div className={Styles.error}>Your browser does not support required media features.</div>
    }

    if (!video || !zoomClient) {
        return <div className={Styles.error}>Required context dependencies are missing.</div>
    }

    return (
        <div className={Styles.previewVideo}>
            {errorMessage && <div className={Styles.error}>{errorMessage}</div>}
            <video
                ref={videoRef}
                className={!isInVBMode ? Styles.previewVideoShow : ''}
                muted
                autoPlay={false}
                playsInline
            />
            <canvas ref={canvasRef} className={isInVBMode ? Styles.previewVideoShow : ''} />
        </div>
    )
}
